/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import { Home, User, Activity, Calendar, BarChart2, EyeOff, ArrowLeft, LogOut } from 'react-feather';
import { AppButton } from '../ui/buttons';
import { SaveButton } from '../ui/buttons/SaveButton';
import { ScribeButton } from '../ScribeButton/ScribeButton';
import { ScribeSummary } from '../ScribeSummary/ScribeSummary';
import { LayoutDashboard } from '../icons/LayoutDashboard';
import { History } from '../icons/History';
import { Sparkles } from '../icons/Sparkles';
import { storage } from '../../utils/storage';
import { ROUTES } from '../../constants/routes';
import { environment } from '../../config/environment';
import { colors } from '../../theme/colors';
import { resolveClinicId, resolveClinicName } from '../../utils/clinicIdentity';
import { SessionsCounter } from '../SessionsBanner/SessionsCounter';
import { formatDoctorDisplayName } from '../../utils/formatDoctorDisplayName';
import { doctorSpecialties, doctorSpecialtyLabel } from '../../utils/specialty';
import { LanguageSelector, useI18n } from '../../i18n';
import './Header.scss';

interface HeaderProps {
  onLogout: () => Promise<void>;
  onEndSession?: () => void;
  endSessionDisabled?: boolean;
  endSessionTooltip?: string;
  onSave?: () => void;
  saveDisabled?: boolean;
  hasUnsavedChanges?: boolean;
  isSaving?: boolean;
  showSaveButton?: boolean;
  sessionId?: number | null;
  onScribeSuccess?: () => void;
  onScribeError?: (message: string) => void;
  onScribeSummaryError?: (message: string) => void;
  pageType?: 'doctor' | 'dashboard' | 'sessions' | 'profile' | 'admin' | 'super-admin' | 'meeting' | 'clinic-profile' | 'clinic-admin' | 'ai-usage';
  onDashboardClick?: () => void;
  // Doctor page specific props
  onSessionsClick?: () => void;
  onBack?: () => void;
  onHistoryClick?: () => void;
  onChatWithEHRClick?: () => void;
  doctorName?: string;
  doctorSpecialty?: string;
  showDoctorInfo?: boolean;
  showDateTime?: boolean;
  sessionDate?: string | null;
  sessionTime?: string | null;
}

const initialsFrom = (name?: string | null): string => {
  const parts = (name || '').replace(/^Dr\.?\s+/i, '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'D';
  return parts
    .map((part, index) => (index === 0 || index === parts.length - 1 ? part[0] : ''))
    .join('')
    .toUpperCase();
};

const AccountMenu: React.FC<{
  name?: string | null;
  specialties: string[];
  clinic?: string | null;
  onProfile: () => void;
  onLogout: () => void | Promise<void>;
}> = ({ name, specialties, clinic, onProfile, onLogout }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  if (!name) return null;

  return (
    <div className="header-account" ref={containerRef}>
      <button
        type="button"
        className="account-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={formatDoctorDisplayName(name)}
        onClick={() => setOpen((value) => !value)}
      >
        {initialsFrom(name)}
      </button>

      {open && (
        <div className="account-menu">
          <span className="account-name">{formatDoctorDisplayName(name)}</span>
          {specialties.length > 0 && <span className="account-specialty">{specialties.join(' · ')}</span>}
          {clinic && <span className="account-clinic">{clinic}</span>}

          <button
            type="button"
            className="account-profile"
            onClick={() => {
              setOpen(false);
              onProfile();
            }}
          >
            <User size={14} />
            <span>{t('header.profile')}</span>
          </button>
          <button
            type="button"
            className="account-logout"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            <LogOut size={14} />
            <span>{t('header.logout')}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export const Header: React.FC<HeaderProps> = ({
  onLogout,
  onEndSession,
  endSessionDisabled = false,
  endSessionTooltip,
  onSave,
  saveDisabled = false,
  hasUnsavedChanges = false,
  isSaving = false,
  showSaveButton = false,
  sessionId = null,
  onScribeSuccess,
  onScribeError,
  onScribeSummaryError,
  pageType = 'doctor',
  onDashboardClick: onDashboardClickProp,
  // Doctor page specific props
  onSessionsClick,
  onBack,
  onHistoryClick,
  onChatWithEHRClick,
  doctorName,
  doctorSpecialty,
  showDoctorInfo = true,
  showDateTime = true,
  sessionDate,
  sessionTime
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, dateLocale } = useI18n();
  const authData = storage.getAuthData();
  const currentUser = authData?.user;
  const isAdmin = currentUser?.is_admin;
  const isSuperAdmin = currentUser?.is_super_admin;
  const isMedicalOfficer = currentUser?.is_medical_officer === true;
  const specialtyLabels = doctorSpecialties(currentUser).map(doctorSpecialtyLabel);
  const clinicId = resolveClinicId();

  const [clinicName, setClinicName] = useState<string | null>(currentUser?.clinic_name || null);

  useEffect(() => {
    let cancelled = false;
    const loadClinicName = async () => {
      const name = await resolveClinicName();
      if (!cancelled) {
        setClinicName(name);
      }
    };
    loadClinicName();
    return () => {
      cancelled = true;
    };
  }, [clinicId, currentUser?.clinic_name]);

  // Auto-detect page type if not provided
  const detectPageType = () => {
    const path = location.pathname;
    // Check for view sessions page first (more specific path)
    if (path.includes(ROUTES.MEETING)) return 'meeting';
    if (path.includes(ROUTES.SESSION_VIEW)) return 'doctor';
    if (path.includes(ROUTES.DOCTOR_DASHBOARD)) return 'dashboard';
    if (path.includes(ROUTES.MANAGE_SESSIONS)) return 'sessions';
    if (path.includes(ROUTES.CLINIC_ADMINISTRATION)) return 'clinic-admin';
    if (path.includes(ROUTES.CLINIC_PROFILE)) return 'clinic-profile';
    if (path.includes(ROUTES.PROFILE)) return 'profile';
    if (path.includes(ROUTES.AI_USAGE_DASHBOARD)) return 'ai-usage';
    if (path.includes(ROUTES.SUPER_ADMIN_DASHBOARD)) return 'super-admin';
    if (path.includes(ROUTES.ADMIN_DASHBOARD)) return 'admin';
    return pageType;
  };

  const currentPageType = detectPageType();

  const handleDashboardClick = () => {
    // If a custom dashboard click handler is provided, call it first
    // This allows parent components to clean up state before navigation
    if (onDashboardClickProp) {
      onDashboardClickProp();
    }

    // Context-aware navigation for all user types
    const isMedicalOfficer = currentUser?.is_medical_officer;
    const hasBothRoles = isAdmin && isMedicalOfficer && !isSuperAdmin;
    const currentPath = location.pathname;

    // Super admins can only access super admin routes
    if (isSuperAdmin) {
      navigate(ROUTES.SUPER_ADMIN_DASHBOARD);
    // For users with both admin and medical officer roles, decide based on current context
    } else if (hasBothRoles) {
      // If on profile page, check the state to see where user came from
      if (currentPath === ROUTES.PROFILE) {
        const locationState = location.state as { fromAdmin?: boolean } | null;
        if (locationState?.fromAdmin) {
          navigate(ROUTES.DOCTOR_DASHBOARD);
        } else {
          navigate(ROUTES.DOCTOR_DASHBOARD);
        }
      } else if (currentPath.includes('/admin')) {
        // If currently on admin pages, go back to the unified doctor dashboard
        navigate(ROUTES.DOCTOR_DASHBOARD);
      } else {
        // If on doctor/medical officer pages, go to doctor dashboard
        navigate(ROUTES.DOCTOR_DASHBOARD);
      }
    } else if (isAdmin) {
      navigate(ROUTES.DOCTOR_DASHBOARD);
    } else {
      navigate(ROUTES.DOCTOR_DASHBOARD);
    }
  };

  const handleProfileClick = () => {
    // Pass current context to profile page so it knows where to return
    const currentPath = location.pathname;
    const fromAdmin = currentPath.includes('/admin');
    navigate(ROUTES.PROFILE, { state: { fromAdmin } });
  };

  const handleClinicProfileClick = () => {
    navigate(ROUTES.CLINIC_PROFILE);
  };

  const handleAIUsageClick = () => {
    navigate(ROUTES.AI_USAGE_DASHBOARD);
  };

  const endSessionButton = onEndSession && (
    <Tooltip title={endSessionTooltip ?? ''} arrow>
      <span className="end-session-wrapper">
        <AppButton variant="primary" onClick={onEndSession} disabled={endSessionDisabled} className="end-session-button">
          {t('header.endSession')}
        </AppButton>
      </span>
    </Tooltip>
  );

  // Render different header content based on page type
  if (currentPageType === 'doctor') {
    // Original DoctorPage header - keep exactly as is
    return (
      <header className="doctor-header">
        <div className="header-left">
          <div className="logo-section">
            <img src="/AIGP_logomark.png" alt={t('header.logoAlt')} className="logo" />
            <div className="logo-text-container">
              <h1 className="logo-text">{t('header.pages.consultTitle')}</h1>
              <span className="agent-name">{t('header.pages.consultSubtitle')}</span>
            </div>
          </div>

          {onBack && (
            <AppButton variant="ghost" startIcon={<ArrowLeft size={16} />} onClick={onBack}>{t('header.sessions')}</AppButton>
          )}

          {onSessionsClick && (
            <AppButton variant="ghost" startIcon={<LayoutDashboard />} onClick={onSessionsClick}>{t('header.sessions')}</AppButton>
          )}
        </div>

        <div className="header-right">
          {/* SuperAdmin clinic context indicator (doctor page) */}
          {isSuperAdmin && (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 12px',
                backgroundColor: colors.warningLight,
                border: `1px solid ${colors.warning}`,
                borderRadius: '6px',
                fontSize: '13px',
                color: colors.warningText,
                fontWeight: '500',
              }}>
                {currentUser?.acting_as_doctor_id ? (
                  <>
                    <span>
                      {t('header.actingAs', {
                        name: formatDoctorDisplayName(currentUser?.acting_as_doctor_name, t('common.unknown')),
                        clinic: clinicName || currentUser?.clinic_name || t('common.clinic'),
                      })}
                    </span>
                    <span style={{ fontSize: '11px', opacity: 0.7 }}>{t('header.impersonation')}</span>
                  </>
                ) : (
                  <>
                    <span>{t('header.viewingClinic', { clinic: clinicName || currentUser?.clinic_name || t('common.clinic') })}</span>
                    <span style={{ fontSize: '11px', opacity: 0.7 }}>{t('header.readOnly')}</span>
                  </>
                )}
              </div>
              <AppButton variant="ghost" startIcon={<EyeOff size={16} />} onClick={() => {
                  const fresh = storage.getAuthData();
                  if (fresh?.user) {
                    fresh.user.clinic_id = undefined;
                    fresh.user.clinic_name = undefined;
                    fresh.user.acting_as_doctor_id = undefined;
                    fresh.user.acting_as_doctor_name = undefined;
                    storage.setAuthData(fresh);
                  }
                  navigate(ROUTES.SUPER_ADMIN_DASHBOARD);
                }}>{t('header.stopViewing')}</AppButton>
            </>
          )}

          <div className="header-context">
            {showDateTime && sessionDate && (
              <div className="datetime-section">
                <span className="datetime-text">
                  {new Date(sessionDate).toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' })}
                  {sessionTime ? `, ${sessionTime.slice(0, 5)}` : ''}
                </span>
              </div>
            )}
          </div>


          <LanguageSelector />

          <div className="header-action-group">
            {onChatWithEHRClick && (
              <AppButton variant="secondary" startIcon={<Sparkles />} onClick={onChatWithEHRClick}>{t('header.chatWithEhr')}</AppButton>
            )}

            {showSaveButton && onSave && (
              <SaveButton
                onClick={onSave}
                disabled={saveDisabled}
                hasUnsavedChanges={hasUnsavedChanges}
              />
            )}

            {endSessionButton}
          </div>

          <AccountMenu
            name={currentUser?.name}
            specialties={specialtyLabels}
            clinic={!isSuperAdmin ? clinicName : null}
            onProfile={handleProfileClick}
            onLogout={onLogout}
          />
        </div>
      </header>
    );
  }

  // For other pages, show simplified header with page info
  const getPageTitle = () => {
    const name = formatDoctorDisplayName(currentUser?.name);
    switch (currentPageType) {
      case 'dashboard':
        return { title: t('header.pages.dashboardTitle'), subtitle: t('header.pages.dashboardSubtitle', { name }) };
      case 'sessions':
        return { title: t('header.pages.sessionsTitle'), subtitle: t('header.pages.sessionsSubtitle') };
      case 'meeting':
        return { title: t('header.pages.meetingTitle'), subtitle: t('header.pages.meetingSubtitle') };
      case 'profile':
        return { title: t('header.pages.profileTitle'), subtitle: t('header.pages.profileSubtitle') };
      case 'clinic-profile':
        return { title: t('header.pages.clinicProfileTitle'), subtitle: t('header.pages.clinicProfileSubtitle') };
      case 'clinic-admin':
        return { title: t('header.pages.clinicAdminTitle'), subtitle: t('header.pages.clinicAdminSubtitle', { name: currentUser?.name || t('header.pages.adminFallback') }) };
      case 'admin':
        return { title: t('header.pages.adminTitle'), subtitle: t('header.pages.adminSubtitle', { name: currentUser?.name || t('header.pages.adminFallback') }) };
      case 'super-admin':
        return { title: t('header.pages.superAdminTitle'), subtitle: t('header.pages.superAdminSubtitle', { name: currentUser?.name || t('header.pages.superAdminFallback') }) };
      case 'ai-usage':
        return { title: t('header.pages.aiUsageTitle'), subtitle: t('header.pages.aiUsageSubtitle') };
      default:
        return { title: t('header.pages.defaultTitle'), subtitle: t('header.pages.defaultSubtitle') };
    }
  };

  const { title, subtitle } = getPageTitle();

  return (
    <header className="doctor-header">
      <div className="header-left">
        <div className="logo-section">
          <img src="/AIGP_logomark.png" alt={t('header.logoAlt')} className="logo" />
          <div className="logo-text-container">
            <h1 className="logo-text">{title}</h1>
            <span className="agent-name">{subtitle}</span>
          </div>
        </div>
      </div>

      <div className="header-right">
        {/* SuperAdmin clinic context indicator */}
        {isSuperAdmin && (currentPageType === 'dashboard' || currentPageType === 'sessions' || currentPageType === 'meeting' || currentPageType === 'clinic-admin') && (
          <>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 12px',
              backgroundColor: colors.warningLight,
              border: `1px solid ${colors.warning}`,
              borderRadius: '6px',
              fontSize: '13px',
              color: colors.warningText,
              fontWeight: '500',
            }}>
              {currentUser?.acting_as_doctor_id ? (
                <>
                  <span>
                    {t('header.actingAs', {
                      name: formatDoctorDisplayName(currentUser?.acting_as_doctor_name, t('common.unknown')),
                      clinic: clinicName || currentUser?.clinic_name || t('common.clinic'),
                    })}
                  </span>
                  <span style={{ fontSize: '11px', opacity: 0.7 }}>{t('header.impersonation')}</span>
                </>
              ) : (
                <>
                  <span>{t('header.viewingClinic', { clinic: clinicName || currentUser?.clinic_name || t('common.clinic') })}</span>
                  <span style={{ fontSize: '11px', opacity: 0.7 }}>{t('header.readOnly')}</span>
                </>
              )}
            </div>
            <AppButton variant="ghost" startIcon={<EyeOff size={16} />} onClick={() => {
                // Clear clinic + impersonation context and go back to super admin dashboard
                const fresh = storage.getAuthData();
                if (fresh?.user) {
                  fresh.user.clinic_id = undefined;
                  fresh.user.clinic_name = undefined;
                  fresh.user.acting_as_doctor_id = undefined;
                  fresh.user.acting_as_doctor_name = undefined;
                  storage.setAuthData(fresh);
                }
                navigate(ROUTES.SUPER_ADMIN_DASHBOARD);
              }}>{t('header.stopViewing')}</AppButton>
          </>
        )}

        {isMedicalOfficer && !isSuperAdmin && currentPageType === 'sessions' && <SessionsCounter />}

        {/* Show Dashboard button when not on dashboard */}
        {currentPageType !== 'dashboard' && currentPageType !== 'admin' && currentPageType !== 'super-admin' && (
          <AppButton variant="ghost" startIcon={<Home size={16} />} onClick={handleDashboardClick}>{t('header.dashboard')}</AppButton>
        )}

        {/* Show Clinic Profile button for admin users only on admin page (clinic is now a tab in clinic-admin) */}
        {(isAdmin && !isSuperAdmin) && (currentPageType === 'admin') && (
          <AppButton variant="ghost" startIcon={<Activity size={16} />} onClick={handleClinicProfileClick}>{t('header.clinic')}</AppButton>
        )}

        {/* AI Usage analytics — super-admin only */}
        {isSuperAdmin && (currentPageType === 'dashboard' || currentPageType === 'super-admin') && (
          <AppButton variant="ghost" startIcon={<BarChart2 size={16} />} onClick={handleAIUsageClick}>{t('header.aiUsage')}</AppButton>
        )}

        {endSessionButton}

        <LanguageSelector />


        <AccountMenu
          name={currentUser?.name}
          specialties={specialtyLabels}
          clinic={!isSuperAdmin ? clinicName : null}
          onProfile={handleProfileClick}
          onLogout={onLogout}
        />
      </div>

    </header>
  );
};
